import classNames from "classnames";
import React from "react";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import "./action-section.scss";

interface IActionSectionEmptyStateBase {
  /** e.g. "No actions yet." / "No screen inputs yet." */
  message: string;
  testId?: string;
  /** Drop the box's own side margins, for a host whose section already pads its content (the
   *  workflow step and event panels). The column's sections leave it off and take its inset. */
  flush?: boolean;
}

/** A section whose items the user adds here: the message is followed by a link that adds one. */
interface IActionSectionEmptyStateWithAction extends IActionSectionEmptyStateBase {
  /** e.g. "Add action" / "Add screen input" */
  actionLabel: string;
  onAdd: () => void;
  hint?: never;
}

/** A section whose items come from somewhere else (Report Parameters are written as @name in label
 *  texts): the message is followed by how to get one, as plain text. */
interface IActionSectionEmptyStateWithHint extends IActionSectionEmptyStateBase {
  hint: string;
  actionLabel?: never;
  onAdd?: never;
}

export type IActionSectionEmptyStateProps = IActionSectionEmptyStateWithAction | IActionSectionEmptyStateWithHint;

/**
 * The empty state shown when a section (Actions, Screen Inputs, Report Parameters, a workflow
 * step's outputs) has zero items: the product's own `KuikaEmptyState` card, embedded in the
 * section and without its icon tile — a panel section is too narrow to spend a 48px tile on, and
 * the section header already names what is missing. The message is its title; a link that adds an
 * item, or a hint saying where items come from, is its body.
 */
const ActionSectionEmptyState: React.FC<IActionSectionEmptyStateProps> = (
  props: IActionSectionEmptyStateProps
): JSX.Element => (
  <div
    className={classNames("kuika-action-section__empty", { "kuika-action-section__empty--flush": props.flush })}
    data-testid={props.testId}
  >
    <KuikaEmptyState
      variant="embedded"
      customName={props.message}
      description={
        props.hint !== undefined ? (
          props.hint
        ) : (
          <button type="button" className="kuika-action-section__empty-action" onClick={props.onAdd}>
            {props.actionLabel}
          </button>
        )
      }
    />
  </div>
);

export default ActionSectionEmptyState;
